import React, { FC, useEffect, useMemo, useState } from "react";
import { SigmaContainer, ZoomControl, FullScreenControl } from "react-sigma-v2";
import { omit, mapValues, keyBy, constant, clamp } from "lodash";

import getNodeProgramImage from "sigma/rendering/webgl/programs/node.image";
import NodeCircleProgram from "sigma/rendering/webgl/programs/node.circle";

import GraphSettingsController from "./GraphSettingsController";
import GraphEventsController from "./GraphEventsController";
import GraphDataController from "./GraphDataController";
import DescriptionPanel from "./DescriptionPanel";
import { WymmoDirectory, FiltersState, DateState } from "../types";
import SearchField from "./SearchField";
import drawLabel from "../canvas-utils";
import GraphTitle from "./GraphTitle";
import TagsPanel from "./TagsPanel";

import "react-sigma-v2/lib/react-sigma-v2.css";
import { GrClose } from "react-icons/gr";
import { BiRadioCircleMarked, BiBookContent } from "react-icons/bi";
import {
  BsArrowsFullscreen,
  BsFullscreenExit,
  BsZoomIn,
  BsZoomOut,
} from "react-icons/bs";
import GraphLayoutController from "./GraphLayoutController";
import DateSelector from "./DateSelector";
import DraggingController from "./DraggingController";
import FocusNodeController from "./FocusNodeController";
import WebGlUnavailable from "./WebGlUnavailable";
import { isWebGlAvailable } from "../webgl";

const Root: FC = () => {
  const initialQuery = useMemo(
    () => new URLSearchParams(window.location.search),
    []
  );
  const initialTags = useMemo(() => initialQuery.getAll("tag"), []);
  const initialDate = useMemo(() => initialQuery.get("date"), []);
  const initialFocusedNode = useMemo(() => initialQuery.get("focus"), []);

  const iconsRoot = useMemo(() => initialQuery.get("icons_root"), []);
  const dataUrl = useMemo(() => initialQuery.get("data_url"), []);
  const webGlAvailable = useMemo(isWebGlAvailable, []);

  const [showContents, setShowContents] = useState(false);
  const [dataReady, setDataReady] = useState(false);
  const [dataset, setDataset] = useState<WymmoDirectory | null>(null);
  const [filtersState, setFiltersState] = useState<FiltersState>({
    tags: {},
  });
  const [hoveredNode, setHoveredNode] = useState<string | null>(null);
  const [isLayoutRunning, setIsLayoutRunning] = useState(false);
  const [dateState, setDateState] = useState<DateState>({
    min: 0,
    max: 0,
    date: 0,
  });

  // Load data on mount:
  useEffect(() => {
    fetch(dataUrl!)
      .then((res) => res.json())
      .then((dataset: WymmoDirectory) => {
        const tagsIndex = keyBy(dataset.tags, "key");
        setDataset(dataset);
        setFiltersState({
          tags: initialTags.length
            ? initialTags
              .filter((tag) => tagsIndex[tag])
              .reduce((iter, key) => ({ ...iter, [key]: true }), {})
            : mapValues(tagsIndex, constant(true)),
        });
        requestAnimationFrame(() => setDataReady(true));

        const yearNow = new Date().getFullYear();
        const minDate = Math.min(
          ...dataset.items.map((item) => item.created_in)
        );
        const date = initialDate
          ? clamp(+initialDate, minDate, yearNow)
          : yearNow;
        setDateState({
          min: minDate,
          max: yearNow,
          date,
        });
      });
  }, []);

  if (!webGlAvailable) {
    return (
      <div id="app-root">
        <WebGlUnavailable />
      </div>
    );
  }
  if (!dataset) return null;

  return (
    <div id="app-root" className={showContents ? "show-contents" : ""}>
      <SigmaContainer
        initialSettings={{
          nodeProgramClasses: {
            image: getNodeProgramImage(),
            circle: NodeCircleProgram,
          },
          labelRenderer: drawLabel,
          defaultNodeType: "image",
          labelDensity: 1,
          labelGridCellSize: 60,
          labelRenderedSizeThreshold: 10,
          labelFont: "Lato, sans-serif",
          zIndex: true,
          itemSizesReference: "positions",
          zoomToSizeRatioFunction:
            window.innerWidth < 1000 && window.innerHeight < 1000
              ? (n) => n
              : Math.sqrt,
        }}
        className="react-sigma"
      >
        <GraphSettingsController hoveredNode={hoveredNode} />
        <GraphEventsController setHoveredNode={setHoveredNode} />
        <GraphDataController
          dataset={dataset}
          filters={filtersState}
          date={dateState.date}
          iconsRoot={iconsRoot!}
        />
        <GraphLayoutController
          filters={filtersState}
          date={dateState}
          setIsRunning={setIsLayoutRunning}
        />
        <DraggingController dataset={dataset} disabled={isLayoutRunning} />
        {dataReady && initialFocusedNode && (
          <FocusNodeController node={initialFocusedNode} isLayoutRunning={isLayoutRunning} />
        )}

        {dataReady && (
          <>
            <div className="controls">
              <div className="ico">
                <button
                  type="button"
                  className="show-contents"
                  onClick={() => setShowContents(true)}
                  title="Show caption and description"
                >
                  <BiBookContent />
                </button>
              </div>
              <FullScreenControl
                className="ico"
                customEnterFullScreen={<BsArrowsFullscreen />}
                customExitFullScreen={<BsFullscreenExit />}
              />
              <ZoomControl
                className="ico"
                customZoomIn={<BsZoomIn />}
                customZoomOut={<BsZoomOut />}
                customZoomCenter={<BiRadioCircleMarked />}
              />
            </div>
            <div className="contents">
              <div className="ico">
                <button
                  type="button"
                  className="ico hide-contents"
                  onClick={() => setShowContents(false)}
                  title="Show caption and description"
                >
                  <GrClose />
                </button>
              </div>
              <GraphTitle
                filters={filtersState}
                date={dateState.date}
                dataset={dataset}
              />
              <div className="panels">
                <SearchField filters={filtersState} />
                <DescriptionPanel />
                <TagsPanel
                  tags={dataset.tags}
                  filters={filtersState}
                  iconsRoot={iconsRoot!}
                  setTags={(tags) =>
                    setFiltersState((filters) => ({
                      ...filters,
                      tags,
                    }))
                  }
                  toggleTag={(tag) => {
                    setFiltersState((filters) => ({
                      ...filters,
                      tags: filters.tags[tag]
                        ? omit(filters.tags, tag)
                        : { ...filters.tags, [tag]: true },
                    }));
                  }}
                />
              </div>
            </div>
            <div className="range-wrapper">
              <DateSelector
                {...dateState}
                onChange={(newDate) =>
                  setDateState((state) => ({ ...state, date: newDate }))
                }
              />
            </div>
          </>
        )}
      </SigmaContainer>
    </div>
  );
};

export default Root;
